/**
 * Parse EPUB navigation (TOC) from either EPUB3 NAV or EPUB2 NCX.
 */

export interface NavItem {
  id: string;
  href: string;
  label: string;
  subitems: NavItem[];
  parent?: string;
}

export interface Landmark {
  href: string;
  label: string;
  type: string;
}

export interface NavigationResult {
  toc: NavItem[];
  tocByHref: Record<string, number>;
  tocById: Record<string, number>;
  landmarks: Landmark[];
  landmarksByType: Record<string, Landmark>;
}

function parseNavItem(li: Element, parent?: string): NavItem {
  const a = li.querySelector(":scope > a") ?? li.querySelector(":scope > span");
  const id = a?.getAttribute("id") ?? li.getAttribute("id") ?? "";
  const href =
    (a?.tagName.toLowerCase() === "a" ? a.getAttribute("href") : "") ?? "";
  const label = a?.textContent?.trim() ?? "";

  const nestedOl = li.querySelector(":scope > ol");
  const subitems: NavItem[] = [];
  if (nestedOl) {
    for (const childLi of Array.from(
      nestedOl.querySelectorAll(":scope > li"),
    )) {
      subitems.push(parseNavItem(childLi, id));
    }
  }

  return { id, href, label, subitems, parent };
}

function parseNav(doc: Document): NavItem[] {
  // Find <nav epub:type="toc"> or <nav> with role/type containing toc
  let nav = doc.querySelector('nav[*|type="toc"]');
  if (!nav) {
    // Fallback: find nav with epub:type attribute containing "toc"
    const navs = doc.querySelectorAll("nav");
    for (const n of Array.from(navs)) {
      for (const attr of Array.from(n.attributes)) {
        if (attr.name.endsWith(":type") && attr.value === "toc") {
          nav = n;
          break;
        }
      }
      // Also try the standard attribute
      if (!nav && n.getAttribute("epub:type") === "toc") {
        nav = n;
      }
    }
  }
  if (!nav) return [];

  const ol = nav.querySelector("ol");
  if (!ol) return [];

  return Array.from(ol.querySelectorAll(":scope > li")).map((li) =>
    parseNavItem(li),
  );
}

function parseNcxItem(navPoint: Element): NavItem {
  const content = navPoint.querySelector("content");
  const navLabel = navPoint.querySelector("navLabel");
  const id = navPoint.getAttribute("id") ?? "";
  const href = content?.getAttribute("src") ?? "";
  const label = navLabel?.textContent?.trim() ?? "";

  // Check if parent is another navPoint
  const parentEl = navPoint.parentElement;
  const parent =
    parentEl?.tagName === "navPoint"
      ? (parentEl.getAttribute("id") ?? "")
      : undefined;

  // Recurse into child navPoints
  const subitems: NavItem[] = [];
  for (const child of Array.from(navPoint.children)) {
    if (child.tagName === "navPoint") {
      subitems.push(parseNcxItem(child));
    }
  }

  return { id, href, label, subitems, parent };
}

function parseNcx(doc: Document): NavItem[] {
  const navMap = doc.querySelector("navMap");
  if (!navMap) return [];
  // Only get direct child navPoints (children handle their own nesting)
  return Array.from(navMap.children)
    .filter((el) => el.tagName === "navPoint")
    .map((np) => parseNcxItem(np));
}

function parseLandmarks(doc: Document): Landmark[] {
  let nav = doc.querySelector('nav[*|type="landmarks"]');
  if (!nav) {
    const navs = doc.querySelectorAll("nav");
    for (const n of Array.from(navs)) {
      for (const attr of Array.from(n.attributes)) {
        if (attr.name.endsWith(":type") && attr.value === "landmarks") {
          nav = n;
          break;
        }
      }
    }
  }
  if (!nav) return [];

  return Array.from(nav.querySelectorAll("a")).map((a) => ({
    href: a.getAttribute("href") ?? "",
    label: a.textContent?.trim() ?? "",
    type:
      a.getAttribute("epub:type") ??
      a.getAttributeNS("http://www.idpf.org/2007/ops", "type") ??
      "",
  }));
}

function buildIndices(
  toc: NavItem[],
  tocByHref: Record<string, number>,
  tocById: Record<string, number>,
  counter = { i: 0 },
): void {
  for (const item of toc) {
    const index = counter.i++;
    if (item.href) {
      tocByHref[item.href] = index;
      // Also index without fragment
      const noFrag = item.href.split("#")[0];
      if (noFrag && noFrag !== item.href) tocByHref[noFrag] = index;
    }
    if (item.id) tocById[item.id] = index;
    if (item.subitems.length)
      buildIndices(item.subitems, tocByHref, tocById, counter);
  }
}

export function parseNavigation(
  doc: Document,
  isNcx: boolean,
): NavigationResult {
  const toc = isNcx ? parseNcx(doc) : parseNav(doc);
  const landmarks = isNcx ? [] : parseLandmarks(doc);

  const tocByHref: Record<string, number> = {};
  const tocById: Record<string, number> = {};
  buildIndices(toc, tocByHref, tocById);

  const landmarksByType: Record<string, Landmark> = {};
  for (const lm of landmarks) {
    if (lm.type) landmarksByType[lm.type] = lm;
  }

  return { toc, tocByHref, tocById, landmarks, landmarksByType };
}
